import { PracticeTemplate } from '@cibo/core'
import { usePermissions } from '@cibo/profile'
import AddIcon from '@mui/icons-material/Add'
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

interface TemplateEditorSidebarProps {
  templates: PracticeTemplate[]
  isLoading: boolean
  selectedId: string | 'new'
  onSelect: (id: string | 'new') => void
  draftName: string
  onDraftNameChange: (name: string) => void
  nameError: string | null
  canEdit: boolean
  onSave: () => void
  isAdvisorOrAdmin?: boolean
  farmerUserId?: number
  authUserId?: number
}

export const TemplateEditorSidebar = ({
  templates,
  isLoading,
  selectedId,
  onSelect,
  draftName,
  onDraftNameChange,
  nameError,
  canEdit,
  onSave,
  isAdvisorOrAdmin,
  farmerUserId,
  authUserId,
}: TemplateEditorSidebarProps) => {
  const { isDebugUser } = usePermissions()
  const { t } = useTranslation('@cibo/programs/TemplateEditorModal')

  // "Any Farmer": templates owned by the logged-in advisor/admin — applicable to any farmer
  const myTemplates = isAdvisorOrAdmin
    ? templates.filter(tmpl => tmpl.owner === authUserId)
    : templates

  // "This Farmer": templates owned by the contextual enrollment owner
  const farmerTemplates = farmerUserId ? templates.filter(tmpl => tmpl.owner === farmerUserId) : []

  // Orphaned: owner is a non-admin user other than the enrollment owner (debug only)
  const orphanedTemplates =
    isAdvisorOrAdmin && isDebugUser
      ? templates.filter(tmpl => tmpl.owner !== authUserId && tmpl.owner !== farmerUserId)
      : []

  return (
    <Box
      sx={{
        width: 260,
        flexShrink: 0,
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1 }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{t('sidebarTitle')}</Typography>
        <Tooltip title={t('newTemplate')}>
          <IconButton size="small" onClick={() => onSelect('new')} aria-label={t('newTemplate')}>
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <List dense sx={{ overflow: 'auto', flex: 1 }}>
          {selectedId === 'new' && (
            <ListItem sx={{ px: 1 }}>
              <TextField
                autoFocus
                size="small"
                fullWidth
                placeholder={t('templateNamePlaceholder')}
                value={draftName}
                onChange={e => onDraftNameChange(e.target.value)}
                onBlur={onSave}
                error={!!nameError}
                helperText={nameError ?? undefined}
                slotProps={{ htmlInput: { maxLength: 100 } }}
              />
            </ListItem>
          )}

          {isAdvisorOrAdmin ? (
            <>
              {/* "This Farmer" section — only shown when there is a contextual enrollment owner */}
              {farmerUserId && (
                <>
                  <ListSubheader
                    disableSticky
                    data-testid="template-sidebar-section-thisFarmer"
                    sx={{ lineHeight: '32px' }}
                  >
                    {t('thisFarmerSection')}
                  </ListSubheader>
                  {farmerTemplates.length === 0 && (
                    <Box sx={{ px: 3, pb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('noTemplatesYet')}
                      </Typography>
                    </Box>
                  )}
                  {farmerTemplates.map(tmpl => (
                    <TemplateItem
                      key={tmpl.id}
                      template={tmpl}
                      selectedId={selectedId}
                      draftName={draftName}
                      onDraftNameChange={onDraftNameChange}
                      onSave={onSave}
                      canEdit={canEdit}
                      onSelect={onSelect}
                      indented={true}
                    />
                  ))}
                </>
              )}

              {/* "Any Farmer" section — templates owned by the logged-in advisor/admin */}
              <ListSubheader
                disableSticky
                data-testid="template-sidebar-section-anyFarmer"
                sx={{ lineHeight: '32px' }}
              >
                {t('anyFarmerSection')}
              </ListSubheader>
              {myTemplates.length === 0 && (
                <Box sx={{ px: 3, pb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('noTemplatesYet')}
                  </Typography>
                </Box>
              )}
              {myTemplates.map(tmpl => (
                <TemplateItem
                  key={tmpl.id}
                  template={tmpl}
                  selectedId={selectedId}
                  draftName={draftName}
                  onDraftNameChange={onDraftNameChange}
                  onSave={onSave}
                  canEdit={canEdit}
                  onSelect={onSelect}
                  indented={true}
                />
              ))}

              {/* Orphaned templates — debug users only */}
              {orphanedTemplates.length > 0 && (
                <>
                  <ListSubheader
                    disableSticky
                    data-testid="template-sidebar-section-otherUsers"
                    sx={{
                      lineHeight: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: theme => alpha(theme.palette.error.main, 0.2),
                    }}
                  >
                    <BugReportOutlinedIcon fontSize="small" color="error" />
                    {t('otherUsersSection')}
                  </ListSubheader>
                  {orphanedTemplates.map(tmpl => (
                    <TemplateItem
                      key={tmpl.id}
                      template={tmpl}
                      selectedId={selectedId}
                      draftName={draftName}
                      onDraftNameChange={onDraftNameChange}
                      onSave={onSave}
                      canEdit={canEdit}
                      onSelect={onSelect}
                      errorBackground={true}
                    />
                  ))}
                </>
              )}
            </>
          ) : (
            <>
              {templates.length === 0 && selectedId !== 'new' && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('noTemplatesYet')}
                  </Typography>
                </Box>
              )}
              {templates.map(tmpl => (
                <TemplateItem
                  key={tmpl.id}
                  template={tmpl}
                  selectedId={selectedId}
                  draftName={draftName}
                  onDraftNameChange={onDraftNameChange}
                  onSave={onSave}
                  canEdit={canEdit}
                  onSelect={onSelect}
                />
              ))}
            </>
          )}
        </List>
      )}
    </Box>
  )
}

const TemplateItem = ({
  template,
  errorBackground = false,
  indented = false,
  selectedId,
  draftName,
  onDraftNameChange,
  onSave,
  canEdit,
  onSelect,
  nameError,
}: {
  template: PracticeTemplate
  errorBackground?: boolean
  indented?: boolean
  selectedId: string
  draftName: string
  onDraftNameChange: (value: string) => void
  onSave: () => void
  canEdit: boolean
  onSelect: (id: string) => void
  nameError?: string
}) =>
  selectedId === template.id ? (
    <ListItem key={template.id} sx={{ pl: indented ? 3 : 1, pr: 1 }}>
      <TextField
        autoFocus
        size="small"
        fullWidth
        value={draftName}
        onChange={e => onDraftNameChange(e.target.value)}
        onBlur={onSave}
        error={!!nameError}
        helperText={nameError ?? undefined}
        disabled={!canEdit}
        slotProps={{ htmlInput: { maxLength: 100 } }}
      />
    </ListItem>
  ) : (
    <ListItemButton
      key={template.id}
      data-testid={`template-sidebar-item-${template.id}`}
      onClick={() => onSelect(template.id)}
      sx={{
        pl: indented ? 3 : 2,
        ...(errorBackground ? { bgcolor: theme => alpha(theme.palette.error.main, 0.2) } : {}),
      }}
    >
      <ListItemText
        primary={template.name}
        slotProps={{
          primary: {
            noWrap: true,
            sx: { textOverflow: 'ellipsis' },
          },
        }}
      />
    </ListItemButton>
  )
